import { isAxiosError } from 'axios'

// One place that turns a thrown request failure into text safe to show a user.
// The API's error envelope is `{ error: { code, message } }`, but some paths return
// a flat `{ message }` or `{ error: "…" }`, so all three shapes are handled here
// rather than re-derived at each call site.

/**
 * Pull the server's actionable message out of an error, ALWAYS as a string.
 *
 * Never returns a non-string: rendering an object as a React child crashes the
 * page, so a malformed envelope falls back to the transport message and finally
 * to `fallback`.
 *
 * @param {unknown} error - The thrown value from a request (axios error or otherwise).
 * @param {string} [fallback] - Text used when nothing usable can be extracted.
 * @returns {string} A human-readable message.
 */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
	if (isAxiosError(error)) {
		const data = error.response?.data as { message?: unknown; error?: unknown } | undefined
		const nested =
			data?.error && typeof data.error === 'object' ? (data.error as { message?: unknown }).message : data?.error
		// Each candidate is type-checked rather than trusted: a drifted or partial
		// envelope must fall through to the next candidate, never reach a caller as a
		// non-string.
		return firstString(data?.message, nested, error.message) ?? fallback
	}
	return error instanceof Error ? error.message : fallback
}

/**
 * The HTTP status a request failed with, when there was a response at all.
 *
 * Lets a caller separate an authorization refusal or a validation rejection
 * from a transport failure without re-deriving the axios shape at each site.
 *
 * @param {unknown} error - The thrown value from a request.
 * @returns {number | undefined} The response status, or `undefined` if there was none.
 */
export function apiErrorStatus(error: unknown): number | undefined {
	return isAxiosError(error) ? error.response?.status : undefined
}

/**
 * First candidate that is a non-empty string.
 *
 * @param {unknown[]} candidates - Values in order of preference.
 * @returns {string | undefined} The first usable string, or `undefined`.
 */
function firstString(...candidates: unknown[]): string | undefined {
	for (const candidate of candidates) {
		if (typeof candidate === 'string' && candidate.length > 0) return candidate
	}
	return undefined
}
